import { formatFileSize, MAX_UPLOAD_BYTES } from '@/helpers/file-size';
import { isImageFile, isPdfFile, isVideoFile } from '@/helpers/helpers';
import { FSRAttachment, FSRDropdownOption } from '@/Interface/fsrManagementType';
import { PlantOwner } from '@/Interface/ticketManagementType';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { BsDatepickerConfig } from 'ngx-bootstrap/datepicker';

/**
 * The Field Service Report field set.
 *
 * Presentational only — the parent owns the FormGroup and the submit call, so
 * the same markup serves both the FSR page (Preventive) and the ticket modal
 * (Corrective) without duplicating it.
 */
@Component({
  selector: 'app-fsr-form',
  templateUrl: './fsr-form.component.html',
  styleUrls: ['./fsr-form.component.scss'],
})
export class FsrFormComponent {
  @Input() form!: FormGroup;
  @Input() plantList: any[] = [];
  @Input() jobVisitTypes: FSRDropdownOption[] = [];
  @Input() complaintTypes: FSRDropdownOption[] = [];
  @Input() customerList: PlantOwner[] = [];
  @Input() staffList: PlantOwner[] = [];
  @Input() files: File[] = [];
  @Input() existingAttachments: FSRAttachment[] = [];

  /** Identity fields, shown read-only once the server has assigned them. */
  @Input() reportSerialNo: string | null = null;
  @Input() jobId: string | null = null;

  /** Corrective FSRs inherit the ticket's plant, so the picker is locked. */
  @Input() plantLocked = false;

  @Output() plantChange = new EventEmitter<any>();
  /** Emits a FileList so the click and drag-and-drop paths look identical to parents. */
  @Output() fileChange = new EventEmitter<FileList>();
  @Output() fileRemove = new EventEmitter<number>();

  /**
   * Panel capacity is a repeatable (No. of Panels x Panel Capacity) row —
   * an installation can mix panel types. The parent owns the FormArray
   * mutation (push/removeAt), mirroring how fileChange/fileRemove already
   * work for the attachment pills.
   */
  @Output() addPanelGroup = new EventEmitter<void>();
  @Output() removePanelGroup = new EventEmitter<number>();

  isDragging = false;

  readonly MAX_UPLOAD_BYTES = MAX_UPLOAD_BYTES;
  readonly formatFileSize = formatFileSize;
  readonly isImageFile = isImageFile;
  readonly isVideoFile = isVideoFile;
  readonly isPdfFile = isPdfFile;

  readonly visitDateConfig: Partial<BsDatepickerConfig> = {
    showWeekNumbers: false,
    dateInputFormat: 'DD/MM/YYYY',
    adaptivePosition: true,
  };

  /** Cached so bsDatepicker gets a stable Date reference between change-detection runs. */
  private visitDateCache: { raw: string | null; date: Date | undefined } = { raw: null, date: undefined };

  /** The visit_date control holds 'YYYY-MM-DD'; parsed as a local date to avoid a UTC day shift. */
  get visitDate(): Date | undefined {
    const raw: string | null = this.form.get('visit_date')?.value || null;
    if (raw !== this.visitDateCache.raw) {
      const [y, m, d] = (raw || '').split('-').map(Number);
      this.visitDateCache = { raw, date: raw && y && m && d ? new Date(y, m - 1, d) : undefined };
    }
    return this.visitDateCache.date;
  }

  onVisitDateChange(date: Date | null | undefined): void {
    const control = this.form.get('visit_date');
    if (!control) {
      return;
    }
    const value = date instanceof Date && !isNaN(date.getTime())
      ? `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`
      : null;
    // bsDatepicker echoes bsValue back on init; only a real change should dirty the form.
    if (value !== (control.value || null)) {
      control.setValue(value);
      control.markAsDirty();
    }
  }

  get customerFieldState(): 'none' | 'single' | 'multiple' {
    if (!this.customerList.length) {
      return 'none';
    }
    return this.customerList.length === 1 ? 'single' : 'multiple';
  }

  get selectedCustomerName(): string {
    const id = this.form.get('customer')?.value;
    return this.customerList.find((owner) => owner.id === id)?.full_name || '';
  }

  get pendingUploadSize(): number {
    return (this.files || []).reduce((sum, file) => sum + (file?.size || 0), 0);
  }

  get pendingUploadExceedsLimit(): boolean {
    return this.pendingUploadSize > MAX_UPLOAD_BYTES;
  }

  invalid(controlName: string): boolean {
    const control = this.form.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  // -- panel capacity rows ----------------------------------------------

  get panelGroups(): FormArray {
    return this.form.get('panel_groups') as FormArray;
  }

  panelGroupInvalid(index: number, controlName: string): boolean {
    const control = this.panelGroups.at(index)?.get(controlName);
    return !!control && control.invalid && control.touched;
  }

  // -- attachment dropzone ---------------------------------------------

  onFileInputChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.fileChange.emit(input.files);
    }
    // Reset so re-picking the same file still fires a change event.
    input.value = '';
  }

  onDragOver(event: DragEvent): void {
    // Both preventDefault calls are required for the drop to fire at all.
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    const dropped = event.dataTransfer?.files;
    if (dropped?.length) {
      this.fileChange.emit(dropped);
    }
  }
}
